import { useEffect, useRef, useState, useCallback } from "react";
import { useGamepadContext } from "../contexts/GamepadProvider";
import { isGamepadMode } from "./useCrossInput";
import { animatedScrollIntoView } from "../utils/smoothScroll";
import { isTV } from "../utils/platform";

interface IUseScrollHorizontal {
  leftIconRef: React.RefObject<HTMLDivElement>;
  rightIconRef: React.RefObject<HTMLDivElement>;
  step?: number;
}

export function useScrollHorizontal({
  leftIconRef,
  rightIconRef,
  step = 500,
}: IUseScrollHorizontal) {
  const rafId = useRef<number | null>(1);
  const { playSFX } = useGamepadContext();
  const [el, setEl] = useState<HTMLDivElement | null>(null);

  const bindContentRef = useCallback((node: HTMLDivElement | null) => {
    setEl(node);
  }, []);

  const setIcons = useCallback(() => {
    if (!el || !leftIconRef.current || !rightIconRef.current || isGamepadMode())
      return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const canScroll = scrollWidth > clientWidth + 1;
    const leftIconInteractive = canScroll && scrollLeft > 0;
    const rightIconInteractive =
      canScroll && scrollLeft + clientWidth < scrollWidth - 1;

    leftIconRef.current.style.opacity = leftIconInteractive ? "1" : "0";
    leftIconRef.current.style.pointerEvents = leftIconInteractive
      ? "auto"
      : "none";
    rightIconRef.current.style.opacity = rightIconInteractive ? "1" : "0";
    rightIconRef.current.style.pointerEvents = rightIconInteractive
      ? "auto"
      : "none";
  }, [el, leftIconRef, rightIconRef]);

  const onScrollDirection = useCallback(
    (dir: "left" | "right") => {
      if (!el) return;
      playSFX("selection");

      const items = Array.from(el.children).slice(1, -1) as HTMLElement[];
      if (items[0]?.getAttribute("data-scroll-inline") === "center") {
        const next =
          items[findCenteredIdx(el, items) + (dir === "right" ? 1 : -1)];
        if (!next) return;
        animatedScrollIntoView(next, {
          axis: "x",
          inline: "center",
          block: "center",
        });
        return;
      }

      el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
    },
    [el, step, playSFX],
  );

  useEffect(() => {
    if (!el || isTV) return;
    const handleScroll = () => {
      if (rafId.current != null) return;
      rafId.current = requestAnimationFrame(() => {
        setIcons();
        rafId.current = null;
      });
    };

    const handleEnter = () => {
      setIcons();
      rafId.current = null;
    };
    const handleLeave = () => {
      rafId.current = 1;
      if (leftIconRef.current) {
        leftIconRef.current.style.opacity = "0";
        leftIconRef.current.style.pointerEvents = "none";
      }

      if (rightIconRef.current) {
        rightIconRef.current.style.opacity = "0";
        rightIconRef.current.style.pointerEvents = "none";
      }
    };

    el.addEventListener("mouseenter", handleEnter);
    el.addEventListener("mouseleave", handleLeave);
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("mouseenter", handleEnter);
      el.removeEventListener("mouseleave", handleLeave);
      el.removeEventListener("scroll", handleScroll);
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
    };
  }, [el, leftIconRef, rightIconRef, setIcons]);

  return { bindContentRef, onScrollDirection };
}

function findCenteredIdx(el: HTMLElement, items: HTMLElement[]): number {
  const center = el.scrollLeft + el.clientWidth / 2;
  let idx = 0;
  let min = Infinity;
  items.forEach((it, i) => {
    const d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - center);
    if (d < min) {
      min = d;
      idx = i;
    }
  });
  return idx;
}
